import { useCallback, useEffect, useRef } from "react";
import { useCaptureSnapshot } from "./useCaptureSnapshot";
import { useUploadSnapshot } from "./useUploadSnapshot";
import { useSetSnapshotAsAThumbnail } from "./useSetSnapshotAsAThumbnail";
import { getIsPersistingIssueSnapshot } from "@adsk/acc-issues-core/state/redux/ducks/issueSnapshots";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { IssueSnapshotCreateTrigger } from "../../../../utils/analytics/analyticsLib093IssuesEvents";
import { useSnapshotAbortController } from "./useSnapshotAbortController";
import { IssueForSnapshot, IssueSnapshotFlow } from "./types";
import { Issue } from "@adsk/acc-issues-types";

export const useIssueSnapshotFlow = (): IssueSnapshotFlow => {
  const captureSnapshot = useCaptureSnapshot();
  const uploadSnapshot = useUploadSnapshot();
  const setSnapshotAsAThumbnail = useSetSnapshotAsAThumbnail();
  const { getFlowId, abortAllFlows } = useSnapshotAbortController();
  const getIsIssueSnapshotInProgress = useSelector(
    getIsPersistingIssueSnapshot
  );

  const getIsIssueSnapshotInProgressRef = useRef<
    ReturnType<typeof getIsPersistingIssueSnapshot>
  >(getIsIssueSnapshotInProgress);

  useEffect(() => {
    getIsIssueSnapshotInProgressRef.current = getIsIssueSnapshotInProgress;
  }, [getIsIssueSnapshotInProgress]);

  const captureAndUploadSnapshot = useCallback(
    async ({ issue }: { issue: IssueForSnapshot }) => {
      if (getIsIssueSnapshotInProgressRef.current(issue.id)) {
        abortAllFlows(issue.id);
      }
      const flowId = getFlowId(issue.id);
      const snapshotBlob = await captureSnapshot({ issue, flowId });
      const storageUrn = await uploadSnapshot({
        issue,
        snapshotBlob,
        hasMarkups: false,
        flowId,
      });
      return { storageUrn, flowId };
    },
    [getFlowId, captureSnapshot, abortAllFlows, uploadSnapshot]
  );

  const uploadSnapshotAsAThumbnail = useCallback(
    async ({
      issue,
      snapshotBlob,
      hasMarkups,
      trigger,
      flowId: flowIdFromProps,
    }: {
      issue: Issue;
      snapshotBlob: Blob | undefined;
      hasMarkups: boolean;
      trigger: IssueSnapshotCreateTrigger;
      flowId?: string;
    }) => {
      if (
        !flowIdFromProps &&
        getIsIssueSnapshotInProgressRef.current(issue.id)
      ) {
        abortAllFlows(issue.id);
      }
      const flowId = flowIdFromProps ?? getFlowId(issue.id);
      const storageUrn = await uploadSnapshot({
        issue,
        snapshotBlob,
        hasMarkups,
        flowId,
      });
      await setSnapshotAsAThumbnail({
        issue,
        storageUrn,
        hasMarkups,
        trigger,
        flowId,
      });
    },
    [getFlowId, uploadSnapshot, setSnapshotAsAThumbnail, abortAllFlows]
  );

  const captureSnapshotAsAThumbnail = useCallback(
    async ({
      issue,
      hasMarkups,
      trigger,
    }: {
      issue: Issue;
      hasMarkups: boolean;
      trigger: IssueSnapshotCreateTrigger;
    }) => {
      if (getIsIssueSnapshotInProgressRef.current(issue.id)) {
        abortAllFlows(issue.id);
      }
      const flowId = getFlowId(issue.id);
      const snapshotBlob = await captureSnapshot({ issue, flowId });
      if (snapshotBlob) {
        await uploadSnapshotAsAThumbnail({
          issue,
          snapshotBlob,
          hasMarkups,
          trigger,
          flowId,
        });
      }
    },
    [getFlowId, captureSnapshot, abortAllFlows, uploadSnapshotAsAThumbnail]
  );

  return {
    captureSnapshot,
    uploadSnapshotAsAThumbnail,
    captureSnapshotAsAThumbnail,
    captureAndUploadSnapshot,
    uploadSnapshot,
    setSnapshotAsAThumbnail,
    getFlowId,
  };
};
